import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react'

import type {
  ContactApplication,
  ContactBusinessUnit,
  ContactReason,
} from '@/modules/contato/pages/motivo-de-contato/contactReasonFormResolver'

type FormData = {
  cpf: string
  fullName: string
  email: string
  emailConfirmation: string
  phone: string
  messageHasNoAccount?: boolean
  messageQrCode?: string
  cnpj: string
  companyName: string
  messageBody: string
  reasonBu?: ContactBusinessUnit
  reasonReason?: ContactReason
  reasonApplication?: ContactApplication
  reasonHasNoTravelId?: boolean
  reasonTravelId?: string
  role?: 'admin' | 'employee'
  attachments?: File[]
  attachmentUrls?: string[]
  zipCode: string
  address: string
  addressNumber: string
  complement?: string
  neighborhood: string
  city: string
  state: string
  referencePoint?: string
  recipientName: string
  recipientEmail: string
  recipientPhone: string
  reasonImpersonationConsent: boolean
}

type ContactState = {
  formState: FormData
  setFormState: Dispatch<SetStateAction<FormData>>
  currentStep: number
  setCurrentStep: Dispatch<SetStateAction<number>>
  cancelModalOpen: boolean
  setCancelModalOpen: Dispatch<SetStateAction<boolean>>
  impersonationModalOpen: boolean
  setImpersonationModalOpen: Dispatch<SetStateAction<boolean>>
  resetState: () => void
}

type ContactProviderProps = {
  children: ReactNode
}

export const ContactStateContext = createContext<ContactState | null>(null)

const initialFormState: FormData = {
  cpf: '',
  fullName: '',
  email: '',
  emailConfirmation: '',
  phone: '',
  messageHasNoAccount: false,
  messageQrCode: undefined,
  cnpj: undefined,
  companyName: '',
  messageBody: '',
  reasonBu: undefined,
  reasonReason: undefined,
  reasonApplication: undefined,
  reasonHasNoTravelId: undefined,
  reasonTravelId: undefined,
  role: undefined,
  attachments: [],
  attachmentUrls: [],
  zipCode: '',
  address: '',
  addressNumber: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  referencePoint: '',
  recipientName: '',
  recipientEmail: '',
  recipientPhone: '',
  reasonImpersonationConsent: false,
}

export function ContactProvider({ children }: ContactProviderProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [impersonationModalOpen, setImpersonationModalOpen] = useState(false)
  const [formState, setFormState] = useState<FormData>(initialFormState)

  const resetState = useCallback(() => {
    setFormState(initialFormState)
    setCurrentStep(1)
    setCancelModalOpen(false)
    setImpersonationModalOpen(false)
  }, [])

  return (
    <ContactStateContext.Provider
      value={{
        formState,
        setFormState,
        currentStep,
        setCurrentStep,
        cancelModalOpen,
        setCancelModalOpen,
        impersonationModalOpen,
        setImpersonationModalOpen,
        resetState,
      }}
    >
      {children}
    </ContactStateContext.Provider>
  )
}

export function useContactState() {
  const context = useContext(ContactStateContext)

  if (!context) {
    throw new Error('useContactState must be used within an ContactProvider')
  }

  return context
}
